import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  IconButton,
  Fab,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Videocam as VideocamIcon,
  People as PeopleIcon,
  Favorite as FavoriteIcon,
  Share as ShareIcon,
  Stop as StopIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "../contexts/AuthContext";
import liveStreamService, { LiveStream, LiveStreamStatus, LiveStreamService } from '../services/live-stream-service';
import LiveStreamDiscovery from '../components/LiveStream/LiveStreamDiscovery';
import EmojiReactionSystem from '../components/LiveStream/EmojiReactionSystem';

const LiveStreamPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [liveStreams, setLiveStreams] = useState<LiveStream[]>([]);
  const [scheduledStreams, setScheduledStreams] = useState<LiveStream[]>([]);
  const [myActiveStreams, setMyActiveStreams] = useState<LiveStream[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStreams();
  }, []);

  const loadStreams = async () => {
    try {
      setLoading(true);
      
      // Load public streams
      const response = await liveStreamService.discoverStreams({
        limit: 20,
        includeScheduled: true,
        includeTrending: true,
      });
      
      setLiveStreams(response.liveStreams);
      setScheduledStreams(response.scheduledStreams);
      
      // Load user's own active streams if logged in
      if (user) {
        await loadMyActiveStreams();
      }
    } catch (err) {
      setError('Failed to load live streams');
      console.error('Error loading streams:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMyActiveStreams = async () => {
    try {
      // Get all streams and filter for user's active ones
      const response = await liveStreamService.discoverStreams({
        limit: 100, // Get more to find user's streams
        includeScheduled: true,
        includeTrending: false,
      });
      
      // Filter for user's own active streams (live or scheduled)
      const myStreams = [
        ...response.liveStreams,
        ...response.scheduledStreams
      ].filter(stream => 
        stream.userId === user?.id && 
        (stream.status === 'live' || stream.status === 'scheduled')
      );
      
      setMyActiveStreams(myStreams);
    } catch (err) {
      console.error('Error loading user streams:', err);
    }
  };

  const handleJoinStream = async (streamId: string) => {
    try {
      await liveStreamService.joinStream(streamId);
      navigate(`/live/watch/${streamId}`);
    } catch (err) {
      console.error('Error joining stream:', err);
      setError('Failed to join stream');
    }
  };

  const handleStartBroadcast = () => {
    navigate('/live/broadcast');
  };

  const handleEndStream = async (streamId: string) => {
    try {
      await liveStreamService.endStream(streamId);
      // Reload streams to update the UI
      await loadStreams();
    } catch (err) {
      console.error('Error ending stream:', err);
      setError('Failed to end stream');
    }
  };

  const handleContinueBroadcast = (streamId: string) => {
    // Navigate to broadcast page with existing stream
    navigate(`/live/broadcast?streamId=${streamId}`);
  };

  const formatViewerCount = (count: number): string => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };

  if (loading) {
    return (
      <Box sx={{ py: 4, width: '100%' }}>
        <Typography>Loading live streams...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4, width: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          📺 Live Fashion Streams
        </Typography>
        {user && (
          <Button
            variant="contained"
            startIcon={<VideocamIcon />}
            onClick={handleStartBroadcast}
            sx={{
              background: 'linear-gradient(45deg, #FF6B6B, #4ECDC4)',
              '&:hover': {
                background: 'linear-gradient(45deg, #FF5252, #26C6DA)',
              },
            }}
          >
            Start Broadcasting
          </Button>
        )}
      </Box>

      {error && (
        <Box sx={{ mb: 3, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {/* My Active Streams Section */}
      {user && myActiveStreams.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            🎥 My Active Streams ({myActiveStreams.length})
          </Typography>
          
          <Grid container spacing={3}>
            {myActiveStreams.map((stream) => (
              <Grid item xs={12} sm={6} md={4} key={stream.streamId}>
                <Card 
                  sx={{ 
                    height: '100%',
                    border: '2px solid',
                    borderColor: stream.status === 'live' ? 'error.main' : 'warning.main',
                  }}
                >
                  {/* Stream Thumbnail */}
                  <Box
                    sx={{
                      height: 160,
                      background: stream.status === 'live' 
                        ? 'linear-gradient(45deg, #FF6B6B, #4ECDC4)'
                        : 'linear-gradient(45deg, #f093fb, #f5576c)',
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <VideocamIcon sx={{ fontSize: 40, color: 'white', opacity: 0.8 }} />
                    
                    {/* Status Badge */}
                    <Chip
                      label={stream.status === 'live' ? 'LIVE' : 'SCHEDULED'}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        bgcolor: stream.status === 'live' ? 'error.main' : 'warning.main',
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    />
                    
                    {/* Viewer Count for live streams */}
                    {stream.status === 'live' && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          display: 'flex',
                          alignItems: 'center',
                          bgcolor: 'rgba(0,0,0,0.6)',
                          borderRadius: 1,
                          px: 1,
                          py: 0.5,
                        }}
                      >
                        <PeopleIcon sx={{ fontSize: 16, color: 'white', mr: 0.5 }} />
                        <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold' }}>
                          {formatViewerCount(stream.totalViewers)}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                      {stream.title}
                    </Typography>
                    
                    {stream.description && (
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ mb: 2 }}
                      >
                        {stream.description}
                      </Typography>
                    )}

                    {stream.scheduledAt && stream.status === 'scheduled' && (
                      <Typography variant="body2" color="primary" sx={{ mb: 2 }}>
                        Scheduled: {new Date(stream.scheduledAt).toLocaleString()}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {stream.status === 'live' && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleContinueBroadcast(stream.streamId)}
                          sx={{ flex: 1 }}
                        >
                          Continue
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleEndStream(stream.streamId)}
                        sx={{ flex: 1 }}
                      >
                        {stream.status === 'live' ? 'End Stream' : 'Cancel'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Live Streams Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          🔴 Live Now ({liveStreams.length})
        </Typography>
        
        {liveStreams.length === 0 ? (
          <Card sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
            <Typography variant="h6" color="text.secondary">
              No live streams at the moment
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Be the first to start a fashion show!
            </Typography>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {liveStreams.map((stream) => (
              <Grid item xs={12} sm={6} md={4} key={stream.streamId}>
                <Card 
                  sx={{ 
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 3,
                    },
                  }}
                  onClick={() => handleJoinStream(stream.streamId)}
                >
                  {/* Stream Thumbnail */}
                  <Box
                    sx={{
                      height: 200,
                      background: 'linear-gradient(45deg, #667eea, #764ba2)',
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PlayIcon sx={{ fontSize: 60, color: 'white', opacity: 0.8 }} />
                    
                    {/* Live Badge */}
                    <Chip
                      label="LIVE"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        bgcolor: 'error.main',
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    />
                    
                    {/* Viewer Count */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: 'rgba(0,0,0,0.6)',
                        borderRadius: 1,
                        px: 1,
                        py: 0.5,
                      }}
                    >
                      <PeopleIcon sx={{ fontSize: 16, color: 'white', mr: 0.5 }} />
                      <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold' }}>
                        {formatViewerCount(stream.totalViewers)}
                      </Typography>
                    </Box>
                  </Box>

                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                      {stream.title}
                    </Typography>
                    
                    {stream.description && (
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          mb: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {stream.description}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          src={(stream as any).user?.profilePictureUrl}
                          sx={{ width: 32, height: 32, mr: 1 }}
                        >
                          {((stream as any).user?.name || stream.userId).charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="body2" color="text.secondary">
                          {(stream as any).user?.name || `User ${stream.userId.slice(-4)}`}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton size="small" color="primary">
                          <FavoriteIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="primary">
                          <ShareIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Scheduled Streams Section */}
      {scheduledStreams.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            📅 Coming Up ({scheduledStreams.length})
          </Typography>
          
          <Grid container spacing={3}>
            {scheduledStreams.map((stream) => (
              <Grid item xs={12} sm={6} md={4} key={stream.streamId}>
                <Card sx={{ height: '100%' }}>
                  <Box
                    sx={{
                      height: 120,
                      background: 'linear-gradient(45deg, #f093fb, #f5576c)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                    }}
                  >
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                      Scheduled
                    </Typography>
                  </Box>

                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                      {stream.title}
                    </Typography>
                    
                    {stream.scheduledAt && (
                      <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
                        {new Date(stream.scheduledAt).toLocaleString()}
                      </Typography>
                    )}
                    
                    {stream.description && (
                      <Typography variant="body2" color="text.secondary">
                        {stream.description}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Discovery Component */}
      <LiveStreamDiscovery />

      {/* Floating Action Button for Quick Broadcast */}
      {user && (
        <Fab
          color="primary"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            background: 'linear-gradient(45deg, #FF6B6B, #4ECDC4)',
          }}
          onClick={handleStartBroadcast}
        >
          <VideocamIcon />
        </Fab>
      )}
    </Box>
  );
};

export default LiveStreamPage;